import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import TopWedge from "@/components/TopWedge";
import { C, PROGRAM_META } from "@/lib/brand";
import useMarketingPrograms from "@/lib/useMarketingPrograms";
import { useAuth } from "@/lib/auth";

export default function Footer({ prevColor = C.cream, wedgeDir = "right" }) {
  const marketingPrograms = useMarketingPrograms();
  const { consumer } = useAuth();
  const isAuthed = !!consumer;
  // Same gate as the AppShell tab bar — only members with the
  // research access flag see the Research entry point in the footer.
  const hasResearchAccess = consumer?.researchAccess === true;

  // BE is authoritative — when the marketing API list has loaded, trust
  // it: respect `isVisibleOnLanding` and DON'T inject PROGRAM_META
  // fallback tiles. Fallbacks only fill the initial-load empty state.
  const visiblePrograms = (marketingPrograms || []).filter((p) => p?.isVisibleOnLanding !== false);
  const liveSlugs = new Set(visiblePrograms.map((p) => (p.slug || "").toLowerCase()));
  const isInitialLoad = marketingPrograms === null;
  const fallback = isInitialLoad
    ? PROGRAM_META
        .filter((m) => !m.slugMatch.some((x) => Array.from(liveSlugs).some((s) => s.includes(x))))
        .filter((m) => !m.slugMatch.some((x) => x === "sexual-wellness" || x === "sexual"))
        .map((m) => ({
          label: `${m.t}${m.t2 ? " " + m.t2 : ""}`,
          to: `/programs/${m.slugMatch[0]}`,
        }))
    : [];
  const live = visiblePrograms.map((p) => ({
    label: p.name || p.slug,
    to: `/programs/${p.slug}`,
  }));
  const programLinks = [...live, ...fallback];

  const cols = [
    { t: "PROGRAMS", l: programLinks },
    { t: "COMPANY", l: [
      { label: "About", to: "/about" },
      { label: "What we help with", to: "/what-we-help-with" },
      { label: "Affiliates", to: "/affiliates" },
      // Research catalogue link only surfaces for members who have
      // been granted access (BE flag on consumer.researchAccess).
      // Anonymous visitors who land on /research directly via a
      // physician's URL still get the page; we just don't advertise
      // it to anyone in the public footer who isn't already cleared.
      ...(isAuthed && hasResearchAccess ? [{ label: "Research", to: "/research" }] : []),
      { label: "Sign in", to: "/sign-in" },
      { label: "Get access", to: "/get-access" },
    ]},
    { t: "SUPPORT", l: [
      { label: "Help center", to: "/sign-in" },
      { label: "Privacy", to: "/legal/privacy" },
      { label: "Terms", to: "/legal/terms" },
      { label: "Telehealth", to: "/legal/telehealth" },
    ]},
  ];
  return (
    <footer data-testid="public-footer" style={{ background: C.ink, color: C.cream, padding: "96px 24px 32px", position: "relative" }}>
      <TopWedge color={prevColor} dir={wedgeDir} />
      <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr", gap: 36 }} className="grid-cols-1 md:!grid-cols-4">
          <div>
            <Logo light size={28} />
            <p style={{ marginTop: 16, fontSize: 13, color: "rgba(250,246,240,0.65)", lineHeight: 1.6, maxWidth: 280 }}>
              Physician-supervised wellness, designed around how you actually live.
            </p>
          </div>
          {cols.map((c) => (
            <div key={c.t}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peach }}>{c.t}</div>
              <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
                {c.l.map((item, i) => (
                  <Link key={i} to={item.to} style={{ fontSize: 13, color: "rgba(250,246,240,0.85)", textDecoration: "none" }} data-testid={`footer-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 56, paddingTop: 24, borderTop: "1px solid rgba(255,255,255,0.1)", fontSize: 11, color: "rgba(250,246,240,0.5)", letterSpacing: "0.1em" }}>
          © 2026 VOYA9 HEALTH · ALL RIGHTS RESERVED
        </div>
      </div>
    </footer>
  );
}
